import type {
  CareItem,
  CareItemID,
  CareItemType,
} from 'src/replicant/care/care.ruleset';
import {
  ButtonView,
  Color,
  ImageView,
  MovieClip,
  TintFilter,
  View,
} from '@play-co/timestep-core/ui';
import * as Care from 'src/replicant/care';
import { getCareItemAnimationName } from 'src/lib/ui/config/buruburu';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import StateObserver from 'src/StateObserver';
import { FloatAnimator } from 'src/lib/Animator';
import uiConfig from 'src/lib/ui/config';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { waitForAssets } from 'src/lib/utils';
import { petEvolutionAssets } from 'src/loadingGroups';
import sounds from 'src/lib/sounds';
import {
  getUnscaledAnimations,
  petAnimations,
} from 'src/replicant/pet/PetAssets';
import { AccessorySlot } from 'src/replicant/accessory/accessory.ruleset';
import { AnimationName } from '../appearance/appearance.config';
import { getTutorialController } from '../tutorial/TutorialController';
import { captureGenericError } from 'src/lib/sentry';
import { Feature } from 'src/replicant/analytics';

type EntityType = 'pet' | 'careItem';

export interface Entity extends View {
  moving: boolean;
  entityId: string;

  setFlipped(flipped: boolean): void;
}

export const isEntity = (view: View): view is Entity => 'entityId' in view;

const EVOLUTION_GLOW_URL = 'assets/care/evolve';

namespace style {
  export namespace petEntity {
    export const dimensions = {
      width: 400,
      height: 400,
    };
    export const offset = {
      offsetX: 200,
      offsetY: 300,
    };
    export const statusClip = {
      scale: 0.88,
      ...offset,
    };
    export const glowClip = {
      offsetX: -270,
      offsetY: -320,
      scale: 0.88,
    };
  }
}

export class PetEntity extends View implements Entity {
  public static TapEvent = 'Tap';

  public getView() {
    return this.scaleView;
  }

  public get zIndex(): number {
    return this.style.zIndex;
  }

  public set zIndex(value: number) {
    this.updateOpts({
      zIndex: Math.floor(value),
    });
  }

  public moving = false;

  private unscaledClipBack: MovieClip;
  private unscaledClipFront: MovieClip;
  private scaleView: ButtonView;
  public petClip: MovieClip;
  public statusClip: MovieClip;
  private glowClip: MovieClip;
  private glowBackground!: View;
  private glowEffectAnimator: FloatAnimator;

  constructor(
    public entityId: string,
    species: PetSpeciesID | undefined,
  ) {
    super({
      ...style.petEntity.dimensions,
      offsetX: -style.petEntity.offset.offsetX,
      offsetY: -style.petEntity.offset.offsetY,
    });

    this.unscaledClipBack = new MovieClip({
      superview: this,
      ...style.petEntity.offset,
    });

    this.scaleView = new ButtonView({
      superview: this,
      ...style.petEntity.dimensions,
      anchorX: style.petEntity.offset.offsetX,
      anchorY: style.petEntity.offset.offsetY,
      onClick: () => {
        sounds.playSound('tapPet');
        this.emit(PetEntity.TapEvent);
        void getTutorialController().handleEvent('tapPet');
      },
    });

    this.statusClip = new MovieClip({
      superview: this.scaleView,
      ...style.petEntity.statusClip,
    });

    this.petClip = new MovieClip({
      superview: this.scaleView,
      ...style.petEntity.offset,
    });

    this.glowClip = new MovieClip({
      superview: this.scaleView,
      ...style.petEntity.glowClip,
    });

    this.unscaledClipFront = new MovieClip({
      superview: this,
      ...style.petEntity.offset,
    });

    if (species) {
      this.setSpecies(species);
    }

    const filter = new TintFilter(new Color('rgba(255, 255, 255, 0)'));
    this.petClip.setFilter(filter);
    this.glowEffectAnimator = new FloatAnimator((value) => {
      this.glowBackground.style.opacity = value;
      // Float precision locked to 3 to avoid long values like 6.66666666759852e-7 that would break the Color class
      filter.update(new Color(`rgba(255, 255, 255, ${value.toFixed(3)})`));
    });
  }

  public setFlipped(flipped: boolean) {
    this.petClip.updateOpts({ flipX: flipped });
  }

  public setSpecies(species: PetSpeciesID) {
    const animationOpts = petAnimations[species];
    this.petClip.updateOpts(animationOpts);
    this.unscaledClipBack.updateOpts(animationOpts);
    this.unscaledClipFront.updateOpts(animationOpts);
  }

  public setAnimation(pet: string) {
    // Already playing, bail.
    if (this.petClip.isAnimationPlaying(pet)) return;

    this.petClip.loop(pet);

    const { back, front } = getUnscaledAnimations(pet);
    if (back) {
      this.unscaledClipBack.loop(back);
      this.unscaledClipBack.show();
    } else {
      this.unscaledClipBack.stop();
      this.unscaledClipBack.hide();
    }

    if (front) {
      this.unscaledClipFront.loop(front);
      this.unscaledClipFront.show();
    } else {
      this.unscaledClipFront.stop();
      this.unscaledClipFront.hide();
    }
  }

  public setStatusAnimation(status: AnimationName) {
    // Already playing, bail.
    if (
      this.statusClip.style.visible &&
      this.statusClip.isAnimationPlaying(status)
    ) {
      return;
    }
    const locale = StateObserver.getLocale();
    this.statusClip.url = `assets/status/state_${status}_${locale}`;
    this.statusClip.loop(status);
    this.statusClip.show();
  }

  public setStatusAnimationVisible(visible: boolean) {
    this.statusClip.updateOpts({ visible });
  }

  public setAccessory(slot: AccessorySlot, image?: string) {
    const slotMap: Record<AccessorySlot, string> = {
      1: 'placeholder_hat',
      2: 'placeholder_glasses',
      3: 'placeholder_mask',
      4: 'placeholder_neck',
      5: 'placeholder_cape_side',
    };

    // Empty accessory.
    if (!image) {
      this.petClip.removeViewSubstitution(slotMap[slot]);
      return;
    }

    // Add accessory.
    this.petClip.addViewSubstitution(
      slotMap[slot],
      new ImageView({ image, width: 360, height: 320, centerOnOrigin: true }),
    );
  }

  public async setEvolutionEffectsVisible(isVisible: boolean, duration = 3000) {
    if (isVisible) {
      await waitForAssets(petEvolutionAssets, {
        feature: Feature.PET._,
        subFeature: Feature.PET.EVOLUTION_ASSETS,
      });
      // Assigned here rather than in the constructor opts: setting `url` makes
      // MovieClip fetch its data JSON and then its spritesheets, two serial
      // round trips that every launch paid for an effect only evolution uses.
      // `petEvolutionAssets` has just loaded the same movieclip, so this
      // resolves from cache.
      this.glowClip.updateOpts({ url: EVOLUTION_GLOW_URL });
      this.glowClip.play('evolution_vfx_anims_final');
      const { screenSize } = StateObserver.getState().ui;

      this.glowBackground = this.getSuperview()!.addSubview(
        new View({
          tag: 'evolutionGlowBackground',
          ...uiConfig.popups.backgroundDarker,
          width: screenSize.width,
          height: screenSize.height,
        }),
        this,
      );
    }

    await this.glowEffectAnimator.setTarget(isVisible ? 1 : 0, duration);

    if (!isVisible) {
      this.glowBackground?.removeFromSuperview();
    }
  }
}

export class CareItemEntity extends MovieClip implements Entity {
  private static readonly clips: Record<CareItemType, string> = {
    food: 'assets/care/food',
    snack: 'assets/care/snack',
    toy: 'assets/care/toy',
    first_aid: 'assets/care/firstAid',
    invitation: 'assets/care/invitation',
  };

  public moving = false;

  constructor(
    public entityId: string,
    item: CareItem,
  ) {
    super({
      url: CareItemEntity.clips[item.type],
      width: 100,
      offsetX: -50,
      offsetY: 50,
    });

    this.play(getCareItemAnimationName(item.item));
    this.pause();
  }

  public setFlipped(flipped: boolean) {
    this.updateOpts({ flipX: flipped });
  }
}

const entities: Record<string, Entity> = {};

/* eslint-disable no-redeclare */
function getEntityId(type: 'pet', playerId: string): string;
function getEntityId(
  type: 'careItem',
  careItemId: CareItem['id'],
  playerId: string,
): string;
function getEntityId(type: EntityType, id: string, playerId?: string) {
  return `${type}-${id}${playerId ? `-${playerId}` : ''}`;
}

export const fromEntityId = (entityId: string) => {
  let [type, id, ...idParts] = entityId.split('-');
  const playerId = idParts?.join('-');
  if (type !== 'careItem' && playerId) {
    id += `-${playerId}`;
  }

  return {
    type,
    id,
    playerId,
  };
};

function getEntity(entityId: string) {
  return entities[entityId];
}

const spawnedEntities: string[] = [];
let currentOffset = { offsetX: 0, offsetY: 0 };

function createOrGetEntity(entityId: string) {
  const { type, id } = fromEntityId(entityId);
  if (!entities[entityId]) {
    switch (type) {
      case 'pet': {
        const { user } = StateObserver.getState();
        const evolution = Pet.getters.getCurrentEvolution(user);
        entities[entityId] = new PetEntity(entityId, evolution?.species);
        break;
      }

      case 'careItem': {
        const careItemID = id as CareItemID;
        const careItem = Care.ruleset.careItems[careItemID];
        entities[entityId] = new CareItemEntity(entityId, careItem);
        entities[entityId].on('ViewRemoved', () => delete entities[entityId]);

        break;
      }
    }

    const entity = entities[entityId] as OffsetMixin<Entity>;
    // Set the offset for the entity.
    entity._originalOffset = {
      offsetX: entity.style.offsetX,
      offsetY: entity.style.offsetY,
    };
    entity.updateOpts({
      offsetX: entity.style.offsetX + currentOffset.offsetX,
      offsetY: entity.style.offsetY + currentOffset.offsetY,
    });
    spawnedEntities.push(entityId);
  }

  return getEntity(entityId);
}

export const EntityManager = {
  getEntityId,

  createOrGetEntity,

  getEntity,

  createOrGetPlayerPet: () =>
    createOrGetEntity(getEntityId('pet', platform.playerID)) as PetEntity,

  createOrGetFriendsPet: (friend: string) =>
    createOrGetEntity(getEntityId('pet', friend)) as PetEntity,

  createOrGetCareItem: (itemId: CareItemID, playerId = platform.playerID) =>
    createOrGetEntity(
      getEntityId('careItem', itemId, playerId),
    ) as CareItemEntity,

  deleteEntity: (entityId: string) => {
    if (spawnedEntities.indexOf(entityId) !== -1) {
      spawnedEntities.splice(spawnedEntities.indexOf(entityId), 1);
    }
    entities[entityId]?.removeFromSuperview();
    delete entities[entityId];
  },

  setEntitiesOffset: (newOffset: { offsetX: number; offsetY: number }) => {
    currentOffset = newOffset;
    spawnedEntities.forEach((entityId) => {
      const entity = getEntity(entityId) as OffsetMixin<Entity>;
      if (!entity) {
        captureGenericError('Entity not found', { entityId });
        return;
      }
      entity.updateOpts({
        offsetX: entity._originalOffset.offsetX + currentOffset.offsetX,
        offsetY: entity._originalOffset.offsetY + currentOffset.offsetY,
      });
    });
  },

  getCurrentEntitiesOffset: () => currentOffset,
};

type OffsetMixin<T> = T & {
  _originalOffset: { offsetX: number; offsetY: number };
};
